import { memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@altimateai/lego";
import DataTable from "@/components/tables/DataTable";
import { useAppState } from "@/modules/app/useAppContext";
import { TableHeaderType } from "@/components/tables/types";
import { useExplorerFiltersFromUrl } from "../../hooks";
import type { OwnerListItem, PriorityOpportunityResponse } from "../../types";
import { getEffortLabel, getOpportunityStatus } from "../../utils";
import EffortTag from "../effortTag/EffortTag";
import StatusTag from "../statusTag/StatusTag";
import OwnerAssignerDropdown from "../ownerAssignerDropdown/OwnerAssignerDropdown";
import {
  DetailsCell,
  ResourceCell,
  MoneySavingsCell,
  TimeSavingsCell,
} from "../opportunities/cells";

interface PriorityTableProps {
  rows: PriorityOpportunityResponse[];
  ownerList: OwnerListItem[] | null;
  onOwnerChange: (owner: number | string, opportunity_id: string) => void;
}

export const PriorityTable = memo(({ rows, ownerList, onOwnerChange }: PriorityTableProps) => {
  const { datastores = [] } = useAppState();
  const { startDate, endDate } = useExplorerFiltersFromUrl();
  const navigate = useNavigate();

  // Priority click-through goes to the new intermediary detail page,
  // which lists all resources for the user to drill into individually
  // (fixes the resources[0]-only navigation gap, scoped to Priority).
  const navigateToOpportunity = useCallback(
    (row: PriorityOpportunityResponse) => {
      if (row.instance_id == null) return;
      navigate(
        `/discover/opportunity/detail/${encodeURIComponent(row.opportunity_id)}?instance_id=${row.instance_id}`
      );
    },
    [navigate]
  );

  // Mechanical 1-based position in the (already priority-ordered) list. The
  // backend's `priority_rank` is intentionally ignored — the Index column just
  // reflects display order (1, 2, 3, …).
  const indexByOpportunityId = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((row, i) => map.set(row.opportunity_id, i + 1));
    return map;
  }, [rows]);

  const headers: TableHeaderType<PriorityOpportunityResponse>[] = useMemo(
    () => [
      {
        id: 0,
        label: "Index",
        uid: "index",
        width: 48,
        noFloor: true,
        render: (item: PriorityOpportunityResponse) => (
          <div>{indexByOpportunityId.get(item.opportunity_id) ?? "—"}</div>
        ),
      },
      {
        id: 1,
        label: "Details",
        uid: "details",
        width: 350,
        render: (item: PriorityOpportunityResponse) => (
          // Show the primary-resource chip in Details for parity with the
          // All tab (the adjacent Resources column still lists resources too).
          //
          // Navigation lives on this cell only (not the whole row) so
          // that Index/Resources/etc. don't act as click targets.
          <div
            role="button"
            tabIndex={0}
            aria-label={`Open ${item.opportunity_name}`}
            className={"al-cursor-pointer"}
            onClick={() => navigateToOpportunity(item)}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigateToOpportunity(item);
              }
            }}
          >
            <DetailsCell
              resource_name={item.resource_name}
              resource_type={item.resource_type}
              resource_uri={item.resources?.[0]?.resource_uri || ""}
              opportunity_name={item.opportunity_name}
              description={item.description || ""}
              last_seen={item.last_seen}
              first_seen={item.first_seen}
              count={item.count}
              total_count={item.total_count || 0}
            />
          </div>
        ),
      },
      {
        id: 2,
        label: "Resources",
        uid: "resources",
        width: 220,
        render: (item: PriorityOpportunityResponse) => <ResourceCell resources={item.resources} />,
      },
      {
        id: 3,
        label: "Money Savings",
        uid: "money_savings",
        width: 140,
        render: (item: PriorityOpportunityResponse) => (
          <MoneySavingsCell
            savings={{
              min: parseFloat(item.money_savings.min),
              max: parseFloat(item.money_savings.max),
            }}
          />
        ),
      },
      {
        id: 4,
        label: "Time Savings",
        uid: "time_savings",
        width: 140,
        render: (item: PriorityOpportunityResponse) => <TimeSavingsCell time={item.time_savings} />,
      },
      {
        id: 5,
        label: "Effort",
        uid: "effort",
        width: 100,
        render: (item: PriorityOpportunityResponse) => (
          <EffortTag effort={getEffortLabel(item.effort)} />
        ),
      },
      {
        id: 6,
        label: "Owner",
        uid: "owner",
        width: 140,
        render: (item: PriorityOpportunityResponse) => (
          <OwnerAssignerDropdown
            assignedOwner={ownerList
              ?.find(o => o.opportunity_id === item.opportunity_id)
              ?.owner?.toString()}
            setAssignedOwner={(owner: number | string) => {
              onOwnerChange(owner, item.opportunity_id);
            }}
            opportunity_id={item.opportunity_id}
            startDate={startDate?.toISOString()}
            endDate={endDate?.toISOString()}
          />
        ),
      },
      {
        id: 7,
        label: "Status",
        uid: "status",
        width: 120,
        render: (item: PriorityOpportunityResponse) => (
          <StatusTag status={getOpportunityStatus(item.status)} />
        ),
      },
      {
        id: 8,
        label: "Instance",
        uid: "instance",
        width: 200,
        render: (item: PriorityOpportunityResponse) => (
          <div>{datastores?.find(d => Number(d.instance_id) === item.instance_id)?.name}</div>
        ),
      },
    ],
    [
      datastores,
      ownerList,
      onOwnerChange,
      startDate,
      endDate,
      navigateToOpportunity,
      indexByOpportunityId,
    ]
  );

  return (
    <Stack direction={"column"} gap={"lg"} align={"start"} className={"al-w-full"}>
      <DataTable<PriorityOpportunityResponse>
        header={headers}
        data={{ items: rows }}
        disableColumnPinning
        columnPickerPortal="opportunities-column-picker"
      />
    </Stack>
  );
});

PriorityTable.displayName = "PriorityTable";
